(() => {
  const init = (root) => {
    if (!root || root.dataset.ccReady === 'true') return;
    root.dataset.ccReady = 'true';

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const clamp = (value, minimum = 0, maximum = 1) => Math.min(maximum, Math.max(minimum, value));
    const revealItems = [...root.querySelectorAll('.cc-reveal')];

    if (reducedMotion || !('IntersectionObserver' in window)) {
      revealItems.forEach((item) => item.classList.add('is-visible'));
    } else {
      const revealObserver = new IntersectionObserver(
        (entries, observer) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          });
        },
        { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
      );
      revealItems.forEach((item) => revealObserver.observe(item));
    }

    // Never leave essential copy hidden if an observer is delayed or a section
    // is inserted outside the viewport by the Shopify editor.
    window.setTimeout(() => {
      revealItems.forEach((item) => item.classList.add('is-visible'));
    }, 1800);

    if (reducedMotion) {
      root.querySelectorAll('[data-cc-decorative-video] video').forEach((video) => {
        video.pause();
        video.removeAttribute('autoplay');
      });
    }

    const servicesSection = root.querySelector('[data-cc-services-scroll]');
    const servicesSticky = servicesSection?.querySelector('.cc-services__sticky');
    const servicesViewport = root.querySelector('[data-cc-services-viewport]');
    const servicesTrack = root.querySelector('[data-cc-services-track]');
    const gallery = root.querySelector('[data-cc-gallery]');
    const previous = root.querySelector('[data-cc-gallery-prev]');
    const next = root.querySelector('[data-cc-gallery-next]');
    const originalBuildButtons = [...root.querySelectorAll('[data-cc-build]')];
    const buildFilterButtons = [...root.querySelectorAll('[data-cc-build-filter]')];
    const chassisRail = root.querySelector('[data-cc-chassis-rail]');
    const chassisPrevious = root.querySelector('[data-cc-chassis-prev]');
    const chassisNext = root.querySelector('[data-cc-chassis-next]');
    const chassisCurrent = root.querySelector('[data-cc-chassis-current]');
    const chassisCards = [...root.querySelectorAll('[data-cc-chassis-card]')];

    let servicesDistance = 0;
    let servicesTravel = 0;
    let galleryLoop = null;
    let scrollFrame = 0;
    let resizeFrame = 0;
    let chassisFrame = 0;

    const servicesScrollEnabled = () =>
      !reducedMotion &&
      (window.innerWidth < 990 || servicesSection?.dataset.ccServicesDesktopScroll === 'true');

    const measureServices = () => {
      if (!servicesSection || !servicesSticky || !servicesViewport || !servicesTrack) return;

      if (!servicesScrollEnabled()) {
        servicesDistance = 0;
        servicesTravel = 0;
        servicesSection.style.removeProperty('--cc-services-scroll-height');
        servicesViewport.scrollLeft = 0;
        return;
      }

      const lastService = servicesTrack.lastElementChild;
      const contentEnd = lastService
        ? lastService.offsetLeft + lastService.offsetWidth
        : servicesTrack.scrollWidth;

      // End the animation at the last card, not at any trailing width reported by
      // the flex track. Mobile Safari can otherwise keep the sticky area blank.
      servicesDistance = Math.max(
        Math.min(contentEnd - servicesViewport.clientWidth, servicesViewport.scrollWidth - servicesViewport.clientWidth),
        0
      );
      const distanceScale = window.innerWidth < 750 ? 0.52 : 0.85;
      servicesTravel = Math.max(servicesDistance * distanceScale, 1);
      const scrollHeight = servicesSticky.getBoundingClientRect().height + servicesTravel;
      servicesSection.style.setProperty('--cc-services-scroll-height', `${Math.ceil(scrollHeight)}px`);
    };

    const syncServices = () => {
      if (
        !servicesScrollEnabled() ||
        !servicesSection ||
        !servicesSticky ||
        !servicesViewport ||
        servicesDistance <= 0 ||
        servicesTravel <= 0
      ) {
        return;
      }

      const progress = clamp(-servicesSection.getBoundingClientRect().top / servicesTravel);
      servicesViewport.scrollLeft = progress >= 0.995 ? servicesDistance : servicesDistance * progress;
    };

    const makeGalleryClone = (source) => {
      const clone = source.cloneNode(true);
      clone.classList.remove('cc-reveal', 'is-visible');
      clone.dataset.ccClone = 'true';
      clone.dataset.lightboxIndex = source.dataset.lightboxIndex;
      clone.setAttribute('aria-hidden', 'true');
      clone.tabIndex = -1;
      return clone;
    };

    const measureGalleryLoop = () => {
      if (!galleryLoop) return;

      const styles = window.getComputedStyle(gallery);
      const paddingStart = parseFloat(styles.paddingLeft) || 0;
      galleryLoop.loopStart = Math.max(galleryLoop.prependClones[0].offsetLeft - paddingStart, 0);
      galleryLoop.originalStart = Math.max(originalBuildButtons[0].offsetLeft - paddingStart, 0);
      galleryLoop.appendStart = Math.max(galleryLoop.appendClones[0].offsetLeft - paddingStart, 0);
      galleryLoop.span = galleryLoop.appendStart - galleryLoop.originalStart;

      if (!gallery.dataset.ccLoopPositioned) {
        gallery.dataset.ccLoopPositioned = 'true';
        const previousBehavior = gallery.style.scrollBehavior;
        gallery.style.scrollBehavior = 'auto';
        gallery.scrollLeft = galleryLoop.originalStart;
        gallery.style.scrollBehavior = previousBehavior;
      }
    };

    const normalizeGalleryLoop = () => {
      if (!galleryLoop || galleryLoop.wrapping || galleryLoop.span <= 0) return;

      let nextPosition = null;
      if (gallery.scrollLeft >= galleryLoop.appendStart) {
        nextPosition = gallery.scrollLeft - galleryLoop.span;
      } else if (gallery.scrollLeft <= galleryLoop.loopStart + 1) {
        nextPosition = gallery.scrollLeft + galleryLoop.span;
      }

      if (nextPosition === null) return;

      galleryLoop.wrapping = true;
      const previousBehavior = gallery.style.scrollBehavior;
      gallery.style.scrollBehavior = 'auto';
      gallery.scrollLeft = nextPosition;
      gallery.style.scrollBehavior = previousBehavior;
      galleryLoop.wrapping = false;
    };

    originalBuildButtons.forEach((button, index) => {
      button.dataset.lightboxIndex = String(index);
    });

    if (gallery && originalBuildButtons.length > 1 && !buildFilterButtons.length) {

      const cloneCount = Math.min(3, originalBuildButtons.length);
      const prependClones = originalBuildButtons
        .slice(-cloneCount)
        .map(makeGalleryClone);
      const appendClones = originalBuildButtons
        .slice(0, cloneCount)
        .map(makeGalleryClone);
      const prependFragment = document.createDocumentFragment();

      prependClones.forEach((clone) => prependFragment.appendChild(clone));
      gallery.insertBefore(prependFragment, gallery.firstChild);
      appendClones.forEach((clone) => gallery.appendChild(clone));

      galleryLoop = {
        appendClones,
        appendStart: 0,
        loopStart: 0,
        originalStart: 0,
        prependClones,
        span: 0,
        wrapping: false,
      };

      gallery.addEventListener('scroll', () => {
        window.requestAnimationFrame(normalizeGalleryLoop);
      }, { passive: true });

      measureGalleryLoop();
    }

    const scrollGallery = (direction) => {
      if (!gallery) return;
      const card = gallery.querySelector('[data-cc-build]');
      const gap = parseFloat(window.getComputedStyle(gallery).columnGap) || 0;
      const amount = card ? card.getBoundingClientRect().width + gap : gallery.clientWidth * 0.8;
      gallery.scrollBy({ left: amount * direction, behavior: reducedMotion ? 'auto' : 'smooth' });
    };

    if (gallery && previous && next) {
      previous.addEventListener('click', () => scrollGallery(-1));
      next.addEventListener('click', () => scrollGallery(1));
    }

    if (gallery && buildFilterButtons.length) {
      buildFilterButtons.forEach((filterButton) => {
        filterButton.addEventListener('click', () => {
          const filter = filterButton.dataset.ccBuildFilter;
          buildFilterButtons.forEach((button) => {
            const isActive = button === filterButton;
            button.classList.toggle('is-active', isActive);
            button.setAttribute('aria-pressed', String(isActive));
          });
          originalBuildButtons.forEach((card) => {
            card.hidden = filter !== 'all' && card.dataset.category !== filter;
          });
          gallery.scrollTo({ left: 0, behavior: reducedMotion ? 'auto' : 'smooth' });
        });
      });
    }

    const syncChassisCount = () => {
      chassisFrame = 0;
      if (!chassisRail || !chassisCards.length || !chassisCurrent) return;

      const styles = window.getComputedStyle(chassisRail);
      const paddingStart = parseFloat(styles.paddingLeft) || 0;
      const target = chassisRail.scrollLeft + paddingStart;
      let nearestIndex = 0;
      let nearestDistance = Number.POSITIVE_INFINITY;

      chassisCards.forEach((card, index) => {
        const distance = Math.abs(card.offsetLeft - target);
        if (distance < nearestDistance) {
          nearestDistance = distance;
          nearestIndex = index;
        }
      });

      chassisCards.forEach((card, index) => card.toggleAttribute('aria-current', index === nearestIndex));
      chassisCurrent.textContent = String(nearestIndex + 1).padStart(2, '0');
    };

    const requestChassisSync = () => {
      if (chassisFrame) return;
      chassisFrame = window.requestAnimationFrame(syncChassisCount);
    };

    const scrollChassis = (direction) => {
      if (!chassisRail || !chassisCards.length) return;
      const styles = window.getComputedStyle(chassisRail);
      const gap = parseFloat(styles.columnGap) || 0;
      const amount = chassisCards[0].getBoundingClientRect().width + gap;
      chassisRail.scrollBy({ left: amount * direction, behavior: reducedMotion ? 'auto' : 'smooth' });
    };

    if (chassisRail) {
      chassisRail.addEventListener('scroll', requestChassisSync, { passive: true });
      chassisPrevious?.addEventListener('click', () => scrollChassis(-1));
      chassisNext?.addEventListener('click', () => scrollChassis(1));
      syncChassisCount();
    }

    const syncScrollEffects = () => {
      scrollFrame = 0;
      syncServices();
    };

    const requestScrollSync = () => {
      if (scrollFrame) return;
      scrollFrame = window.requestAnimationFrame(syncScrollEffects);
    };

    const measureMotion = () => {
      resizeFrame = 0;
      measureServices();
      measureGalleryLoop();
      syncChassisCount();
      requestScrollSync();
    };

    const requestMeasure = () => {
      if (resizeFrame) return;
      resizeFrame = window.requestAnimationFrame(measureMotion);
    };

    window.addEventListener('scroll', requestScrollSync, { passive: true });
    window.addEventListener('resize', requestMeasure, { passive: true });
    window.addEventListener('load', requestMeasure, { once: true });

    if ('ResizeObserver' in window) {
      const motionObserver = new ResizeObserver(requestMeasure);
      if (servicesTrack) motionObserver.observe(servicesTrack);
      if (gallery) motionObserver.observe(gallery);
      if (chassisRail) motionObserver.observe(chassisRail);
    }

    measureMotion();

    const lightbox = root.querySelector('[data-cc-lightbox]');
    if (!lightbox || !originalBuildButtons.length || typeof lightbox.showModal !== 'function') return;

    const lightboxImage = lightbox.querySelector('img');
    const lightboxCount = lightbox.querySelector('[data-cc-lightbox-count]');
    const lightboxCategory = lightbox.querySelector('[data-cc-lightbox-category]');
    const lightboxTitle = lightbox.querySelector('[data-cc-lightbox-title]');
    const lightboxDescription = lightbox.querySelector('[data-cc-lightbox-description]');
    const lightboxLink = lightbox.querySelector('[data-cc-lightbox-link]');
    const close = lightbox.querySelector('[data-cc-lightbox-close]');
    const lightboxPrevious = lightbox.querySelector('[data-cc-lightbox-prev]');
    const lightboxNext = lightbox.querySelector('[data-cc-lightbox-next]');
    let activeIndex = 0;

    const getVisibleBuilds = () => originalBuildButtons.filter((button) => !button.hidden);

    const showBuild = (index) => {
      const visibleBuilds = getVisibleBuilds();
      if (!visibleBuilds.length) return;
      activeIndex = (index + visibleBuilds.length) % visibleBuilds.length;
      const sourceButton = visibleBuilds[activeIndex];
      const sourceImage = sourceButton.querySelector('img');
      lightboxImage.src = sourceButton.dataset.fullImage || sourceImage.currentSrc || sourceImage.src;
      lightboxImage.alt = sourceImage.alt || `Classic Custom build ${activeIndex + 1}`;
      lightboxCategory.textContent = sourceButton.dataset.buildCategory || '';
      lightboxTitle.textContent = sourceButton.dataset.buildTitle || '';
      lightboxDescription.textContent = sourceButton.dataset.buildDescription || '';
      const link = sourceButton.dataset.buildLink;
      lightboxLink.hidden = !link;
      if (link) {
        lightboxLink.href = link;
        lightboxLink.textContent = sourceButton.dataset.buildLinkLabel || 'Start your build';
      }
      lightboxCount.textContent = `${String(activeIndex + 1).padStart(2, '0')} / ${String(visibleBuilds.length).padStart(2, '0')}`;
    };

    [...root.querySelectorAll('[data-cc-build]')].forEach((button) => {
      button.addEventListener('click', () => {
        const sourceButton = originalBuildButtons[Number(button.dataset.lightboxIndex) || 0];
        const visibleIndex = getVisibleBuilds().indexOf(sourceButton);
        showBuild(visibleIndex < 0 ? 0 : visibleIndex);
        lightbox.showModal();
      });
    });

    close.addEventListener('click', () => lightbox.close());
    lightboxPrevious.addEventListener('click', () => showBuild(activeIndex - 1));
    lightboxNext.addEventListener('click', () => showBuild(activeIndex + 1));
    lightbox.addEventListener('click', (event) => {
      if (event.target === lightbox) lightbox.close();
    });
    lightbox.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') showBuild(activeIndex - 1);
      if (event.key === 'ArrowRight') showBuild(activeIndex + 1);
    });
  };

  const boot = () => document.querySelectorAll('[data-cc-home]').forEach(init);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
  document.addEventListener('shopify:section:load', boot);
})();
